import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, Col, DatePicker, Row, Select, Space, Spin, Table, Tag, Typography,
} from 'antd';
import dayjs from 'dayjs';
import {
  presencasService, unidadeService, equipeService, modalidadeService, turmaService,
} from '../../services/api';
import { usePermissaoRecurso } from '../../hooks/usePermissoes';
import { buildApiErrorMessage } from '../../utils/apiError';
import VisualizarChamadaModal from './VisualizarChamadaModal';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Mesmo teto do backend (presencas_listagem_service.py:validar_intervalo,
// max_dias=62) -- validado aqui só para dar feedback imediato ao usuário;
// o backend continua sendo a autoridade final (rejeita com 422 de
// qualquer forma se este limite for contornado).
const MAX_DIAS_INTERVALO = 62;

// Rótulos/cores conforme aprovado na investigação da Fase A -- mapeamento
// puramente de apresentação, os 4 valores vêm literalmente do contrato
// backend (app/schemas/presencas_admin.py:SITUACAO_PRESENCAS_ADMIN_HTTP).
const SITUACAO_INFO = {
  PREVISTA_NAO_MATERIALIZADA: { label: 'Prevista', color: 'blue' },
  PREVISTA: { label: 'Pendente', color: 'gold' },
  REALIZADA: { label: 'Realizada', color: 'green' },
  CANCELADA: { label: 'Cancelada', color: 'default' },
};

const filtrosPadrao = () => ({
  periodo: [dayjs().startOf('day'), dayjs().startOf('day')],
  unidadeId: undefined,
  equipeId: undefined,
  modalidadeId: undefined,
  turmaId: undefined,
  situacao: undefined,
});

const formatarHora = (valor) => (valor ? valor.slice(0, 5) : '--:--');

// ─────────────────────────────────────────────────────────────
// Fronteira de serialização do estado de navegação (bugfix)
//
// navigate(path, { state }) do React Router, sob BrowserRouter, é
// repassado ao history.pushState/replaceState real do navegador -- que
// clona o `state` via structured clone. Structured clone preserva os
// dados internos de um objeto dayjs (ele vira um objeto plano com os
// mesmos campos), mas NÃO preserva seu protótipo: métodos como
// .isBefore/.format/.diff deixam de existir no objeto restaurado, pois
// dayjs não é um tipo nativo clonável (diferente de Date/Array/Map).
// Isso reproduzia "fim.isBefore is not a function" ao voltar de
// ChamadaAdminDetalhe.js para esta tela.
//
// Regra adotada (única fronteira, não espalhada pelo componente):
// estado de navegação transporta `periodo` como strings 'YYYY-MM-DD'
// (sempre serializáveis, sem perda), nunca objetos dayjs. O estado
// INTERNO do componente (filtrosEmEdicao/filtrosAplicados) continua
// sempre dayjs, como o RangePicker e `intervaloValido` exigem.
// ─────────────────────────────────────────────────────────────

const serializarPeriodoParaState = (periodo) =>
  periodo?.[0] && periodo?.[1] ? [periodo[0].format('YYYY-MM-DD'), periodo[1].format('YYYY-MM-DD')] : null;

const restaurarFiltrosDeState = (filtrosState) => {
  if (!filtrosState?.periodo?.[0] || !filtrosState?.periodo?.[1]) {
    return filtrosPadrao();
  }
  return { ...filtrosState, periodo: [dayjs(filtrosState.periodo[0]), dayjs(filtrosState.periodo[1])] };
};

const PresencasAdmin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading: permissaoLoading, isAdmin, podeVisualizar, podeEditar } = usePermissaoRecurso('presencas');

  // Estado em edição (controla os campos) x estado aplicado (o que
  // realmente foi enviado ao backend na última busca) -- mesmo padrão já
  // usado em Aulas Dadas do Portal (Fase 14E), para nunca disparar
  // request a cada mudança de select e para "Limpar" resetar de fato os
  // parâmetros aplicados, não só os campos visuais.
  //
  // Se o usuário voltou da tela de chamada (Fase D), restaura os filtros
  // que estavam aplicados antes de sair -- state de navegação (React
  // Router), nunca localStorage/backend; se ausente (ex.: acesso direto
  // a /presencas), cai no padrão hoje->hoje normalmente. Reconstrução de
  // dayjs a partir de string acontece só aqui (ver restaurarFiltrosDeState).
  const filtrosDeRetorno = location.state?.filtros;
  const [filtrosEmEdicao, setFiltrosEmEdicao] = useState(() => restaurarFiltrosDeState(filtrosDeRetorno));
  const [filtrosAplicados, setFiltrosAplicados] = useState(() => restaurarFiltrosDeState(filtrosDeRetorno));

  const [encontros, setEncontros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);

  // Linha selecionada para o modal de visualização rápida (somente
  // leitura) -- null = modal fechado. Dobra como flag de "aberto" e como
  // fonte dos dados de apresentação (nome_turma/nome_unidade/etc.) que o
  // GET de chamada não retorna (ver VisualizarChamadaModal.js).
  const [modalVisualizacao, setModalVisualizacao] = useState(null);

  const [unidades, setUnidades] = useState([]);
  const [professores, setProfessores] = useState([]);
  const [modalidades, setModalidades] = useState([]);
  const [turmas, setTurmas] = useState([]);
  // Cada filtro de apoio depende de um grant RBAC PRÓPRIO
  // (unidades/equipe/modalidades/turmas), distinto de "presencas" --
  // decisão de produto explícita: degradar graciosamente por filtro
  // (desabilitar só o que falhar), nunca quebrar a página inteira nem
  // travar a listagem principal de presenças.
  const [filtrosIndisponiveis, setFiltrosIndisponiveis] = useState({});

  useEffect(() => {
    unidadeService.list()
      .then((r) => setUnidades(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFiltrosIndisponiveis((prev) => ({ ...prev, unidade: true })));
    equipeService.list()
      .then((r) => setProfessores(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFiltrosIndisponiveis((prev) => ({ ...prev, professor: true })));
    modalidadeService.list()
      .then((r) => setModalidades(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFiltrosIndisponiveis((prev) => ({ ...prev, modalidade: true })));
    turmaService.list()
      .then((r) => setTurmas(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFiltrosIndisponiveis((prev) => ({ ...prev, turma: true })));
  }, []);

  const carregarEncontros = async (filtros) => {
    setCarregando(true);
    setErro(null);
    try {
      const params = {
        data_inicial: filtros.periodo[0].format('YYYY-MM-DD'),
        data_final: filtros.periodo[1].format('YYYY-MM-DD'),
      };
      if (filtros.unidadeId) params.unidade_id = filtros.unidadeId;
      if (filtros.equipeId) params.equipe_id = filtros.equipeId;
      if (filtros.modalidadeId) params.modalidade_id = filtros.modalidadeId;
      if (filtros.turmaId) params.turma_id = filtros.turmaId;
      if (filtros.situacao) params.situacao = filtros.situacao;

      const response = await presencasService.listarEncontros(params);
      setEncontros(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      const { status, message } = buildApiErrorMessage(error, 'Não foi possível carregar as presenças.');
      setErro({ status, message });
      setEncontros([]);
    } finally {
      setCarregando(false);
    }
  };

  // Carrega uma vez ao abrir a tela -- com os filtros de retorno (se o
  // usuário veio da tela de chamada) ou o padrão hoje->hoje. A listagem
  // em si é sempre GET, nunca materializa nada (ver
  // presencas_listagem_service.py: estritamente read-only).
  useEffect(() => {
    carregarEncontros(filtrosAplicados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const intervaloValido = useMemo(() => {
    const [inicio, fim] = filtrosEmEdicao.periodo || [];
    if (!inicio || !fim) return false;
    if (fim.isBefore(inicio, 'day')) return false;
    return fim.diff(inicio, 'day') + 1 <= MAX_DIAS_INTERVALO;
  }, [filtrosEmEdicao.periodo]);

  const handleFiltrar = () => {
    if (!intervaloValido) return;
    setFiltrosAplicados(filtrosEmEdicao);
    carregarEncontros(filtrosEmEdicao);
  };

  const handleLimpar = () => {
    const padrao = filtrosPadrao();
    setFiltrosEmEdicao(padrao);
    setFiltrosAplicados(padrao);
    carregarEncontros(padrao);
  };

  // Navegação apenas -- NENHUMA chamada PUT/materialização acontece aqui.
  // A tela de destino (Fase D) é quem eventualmente chamará
  // presencasService.prepararEncontro/salvarChamada. `state` carrega a
  // linha (nomes para o cabeçalho, exibição apenas) e os filtros
  // aplicados (para restaurar ao voltar) -- nunca usado como fonte de
  // verdade de autorização pela tela de destino. `periodo` vai como
  // string (ver serializarPeriodoParaState) -- nunca objeto dayjs, que
  // perde seu protótipo ao atravessar history.pushState.
  const handleAcao = useCallback(
    (record) => {
      const state = {
        linha: record,
        filtros: { ...filtrosAplicados, periodo: serializarPeriodoParaState(filtrosAplicados.periodo) },
      };
      if (record.encontro_id) {
        navigate(`/presencas/${record.encontro_id}`, { state });
      } else {
        navigate(`/presencas/nova?turma_id=${record.turma_id}&data=${record.data}`, { state });
      }
    },
    [navigate, filtrosAplicados]
  );

  // Abre o modal de consulta rápida (somente leitura) -- NUNCA para
  // PREVISTA_NAO_MATERIALIZADA (sem encontro_id não existe chamada
  // nenhuma para visualizar; jamais materializa só para consultar).
  const handleVisualizar = useCallback((record) => {
    if (!record.encontro_id) return;
    setModalVisualizacao(record);
  }, []);

  const handleFecharModal = useCallback(() => setModalVisualizacao(null), []);

  // Regra final das ações por situação (uma linha pode ter até 2 botões
  // simultâneos -- Visualizar é sempre independente de Fazer
  // chamada/Corrigir, nunca um substitui o outro):
  //   PREVISTA_NAO_MATERIALIZADA: só "Fazer chamada" (se podeEditar) --
  //     sem Visualizar (não há chamada materializada para consultar) e
  //     sem nenhuma ação se também não tiver podeEditar.
  //   PREVISTA/REALIZADA materializadas: "Visualizar" (se podeVisualizar)
  //     + "Fazer chamada"/"Corrigir" (se podeEditar).
  //   CANCELADA: só "Visualizar" -- backend já rejeita escrita em
  //     encontro CANCELADA (EncontroCanceladoError), frontend nunca
  //     oferece "Corrigir" aqui, apenas reflete a regra já existente.
  const renderAcao = useCallback(
    (record) => {
      const naoMaterializada = record.situacao === 'PREVISTA_NAO_MATERIALIZADA';
      const cancelada = record.situacao === 'CANCELADA';
      const botoes = [];

      if (naoMaterializada) {
        if (podeEditar) {
          botoes.push(
            <Button key="fazer" size="small" type="primary" ghost onClick={() => handleAcao(record)}>
              Fazer chamada
            </Button>
          );
        }
      } else {
        if (podeVisualizar) {
          botoes.push(
            <Button key="ver" size="small" onClick={() => handleVisualizar(record)}>
              Visualizar
            </Button>
          );
        }
        if (podeEditar && !cancelada) {
          const rotulo = record.situacao === 'REALIZADA' ? 'Corrigir' : 'Fazer chamada';
          botoes.push(
            <Button key="editar" size="small" type="primary" ghost onClick={() => handleAcao(record)}>
              {rotulo}
            </Button>
          );
        }
      }

      if (botoes.length === 0) return null;
      return <Space size={4}>{botoes}</Space>;
    },
    [podeEditar, podeVisualizar, handleAcao, handleVisualizar]
  );

  const columns = useMemo(() => [
    {
      title: 'Data', dataIndex: 'data', key: 'data', width: 110,
      render: (valor) => dayjs(valor).format('DD/MM/YYYY'),
    },
    {
      title: 'Horário', key: 'horario', width: 130,
      render: (_, registro) => `${formatarHora(registro.hora_inicio)} - ${formatarHora(registro.hora_fim)}`,
    },
    { title: 'Unidade', dataIndex: 'nome_unidade', key: 'nome_unidade', ellipsis: true },
    { title: 'Modalidade', dataIndex: 'nome_modalidade', key: 'nome_modalidade', ellipsis: true },
    { title: 'Turma', dataIndex: 'nome_turma', key: 'nome_turma', ellipsis: true },
    { title: 'Professor', dataIndex: 'nome_equipe', key: 'nome_equipe', ellipsis: true },
    {
      title: 'Situação', dataIndex: 'situacao', key: 'situacao', width: 130,
      render: (valor) => {
        const info = SITUACAO_INFO[valor] || { label: valor, color: 'default' };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      title: 'Ação', key: 'acao', width: 140, fixed: 'right',
      render: (_, registro) => renderAcao(registro),
    },
    // `renderAcao` já é memoizado com useCallback (deps corretas:
    // podeEditar/podeVisualizar/handleAcao/handleVisualizar, este último
    // fechando sobre filtrosAplicados) -- listar só ele aqui é
    // suficiente e correto: columns só recalcula quando algo que
    // realmente afeta a coluna Ação muda (bug pré-existente corrigido
    // nesta mudança: antes, columns nunca recalculava após o primeiro
    // carregamento de permissões, fazendo "Fazer chamada"/"Corrigir"
    // navegar sempre com os filtros do primeiro render, não os
    // realmente aplicados no momento do clique).
  ], [renderAcao]);

  if (permissaoLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin size="large" tip="Validando permissões..." />
      </div>
    );
  }

  if (!isAdmin && !podeVisualizar) {
    return <Navigate to="/acesso-negado" replace />;
  }

  const algumFiltroIndisponivel = Object.values(filtrosIndisponiveis).some(Boolean);

  return (
    <div>
      <Card>
        <Title level={3} style={{ marginBottom: 4 }}>Presenças</Title>
        <Text type="secondary">Gerencie e acompanhe as chamadas das aulas.</Text>

        <div style={{ marginTop: 24, marginBottom: 16 }}>
          <Row gutter={[12, 12]}>
            <Col>
              <RangePicker
                value={filtrosEmEdicao.periodo}
                format="DD/MM/YYYY"
                allowClear={false}
                onChange={(datas) =>
                  setFiltrosEmEdicao((prev) => ({ ...prev, periodo: datas || filtrosPadrao().periodo }))
                }
              />
            </Col>
            <Col>
              <Select
                style={{ minWidth: 170 }}
                placeholder="Unidade"
                allowClear
                showSearch
                optionFilterProp="children"
                disabled={filtrosIndisponiveis.unidade}
                value={filtrosEmEdicao.unidadeId}
                onChange={(valor) => setFiltrosEmEdicao((prev) => ({ ...prev, unidadeId: valor }))}
              >
                {unidades.map((u) => (
                  <Select.Option key={u.id} value={u.id}>{u.nome_fantasia}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                style={{ minWidth: 170 }}
                placeholder="Professor"
                allowClear
                showSearch
                optionFilterProp="children"
                disabled={filtrosIndisponiveis.professor}
                value={filtrosEmEdicao.equipeId}
                onChange={(valor) => setFiltrosEmEdicao((prev) => ({ ...prev, equipeId: valor }))}
              >
                {professores.map((p) => (
                  <Select.Option key={p.id} value={p.id}>{p.nome}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                style={{ minWidth: 170 }}
                placeholder="Modalidade"
                allowClear
                showSearch
                optionFilterProp="children"
                disabled={filtrosIndisponiveis.modalidade}
                value={filtrosEmEdicao.modalidadeId}
                onChange={(valor) => setFiltrosEmEdicao((prev) => ({ ...prev, modalidadeId: valor }))}
              >
                {modalidades.map((m) => (
                  <Select.Option key={m.id} value={m.id}>{m.nome_modalidade}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                style={{ minWidth: 170 }}
                placeholder="Turma"
                allowClear
                showSearch
                optionFilterProp="children"
                disabled={filtrosIndisponiveis.turma}
                value={filtrosEmEdicao.turmaId}
                onChange={(valor) => setFiltrosEmEdicao((prev) => ({ ...prev, turmaId: valor }))}
              >
                {turmas.map((t) => (
                  <Select.Option key={t.id} value={t.id}>{t.nome_turm}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Select
                style={{ minWidth: 150 }}
                placeholder="Situação"
                allowClear
                value={filtrosEmEdicao.situacao}
                onChange={(valor) => setFiltrosEmEdicao((prev) => ({ ...prev, situacao: valor }))}
              >
                {Object.entries(SITUACAO_INFO).map(([valor, info]) => (
                  <Select.Option key={valor} value={valor}>{info.label}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Space>
                <Button type="primary" onClick={handleFiltrar} disabled={!intervaloValido}>
                  Filtrar
                </Button>
                <Button onClick={handleLimpar}>Limpar</Button>
              </Space>
            </Col>
          </Row>

          {!intervaloValido && (
            <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
              Selecione um período válido (data final não pode ser anterior à inicial; máximo de {MAX_DIAS_INTERVALO} dias).
            </Text>
          )}
          {algumFiltroIndisponivel && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
              Alguns filtros estão indisponíveis para o seu perfil de acesso.
            </Text>
          )}
        </div>

        {erro && (
          <Alert
            type="error"
            showIcon
            message={erro.status === 403 ? 'Acesso negado' : 'Erro ao carregar'}
            description={erro.message}
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" onClick={() => carregarEncontros(filtrosAplicados)}>
                Tentar novamente
              </Button>
            }
          />
        )}

        <Table
          rowKey={(registro) => registro.encontro_id ?? `${registro.turma_id}-${registro.data}`}
          columns={columns}
          dataSource={erro ? [] : encontros}
          loading={carregando}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          locale={{ emptyText: erro ? ' ' : 'Nenhuma aula encontrada para os filtros selecionados.' }}
          scroll={{ x: 960 }}
        />
      </Card>

      <VisualizarChamadaModal
        open={!!modalVisualizacao}
        encontroId={modalVisualizacao?.encontro_id}
        linha={modalVisualizacao}
        onClose={handleFecharModal}
      />
    </div>
  );
};

export default PresencasAdmin;
